import { useState } from "react";
import { bool } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Tables } from "@repo/utils";
import { LOCKED_FEATURES } from "@utils";
import { SettingEntry, useEstimateStructure } from "@tools";
import { useFeatureGuardian } from "@hooks";
import { ActionButton, Dialog, InfoLabel, SortableListV2 } from "@components";
import Grid from "@material-ui/core/Grid";
import { Settings } from "@assets";

export const EstimateSettings = observer(() => {
  const structure = useEstimateStructure();
  const { t } = useTranslation();

  const { checkAccess: checkTimelineAccess, FeatureAlert: FeatureTimelineAlert } = useFeatureGuardian(
    LOCKED_FEATURES.TIMELINE
  );
  const { checkAccess: checkDescriptionAccess, FeatureAlert: FeatureDescriptionAlert } = useFeatureGuardian(
    LOCKED_FEATURES.TEXT_DESCRIPTION
  );

  const [visible, show] = useState(false);
  const { settings } = structure;
  const { usedTables } = settings;

  const handleVisibility = () => show(!visible);

  const handleTableVisibility = (name, visible, setVisibility) => async () => {
    if (name === Tables.TIMELINE) {
      const allowed = await checkTimelineAccess();
      if (!allowed) return;
    }
    if (name === Tables.DESCRIPTION) {
      const allowed = await checkDescriptionAccess();
      if (!allowed) return;
    }
    
    structure.historyManager.startGroup();
    setVisibility(!visible);
    if(name === Tables.TIMELINE) {
      structure.splitWorkTypeResources();
      structure.generateTimeline();
    }
    structure.historyManager.stopGroup();
  };

  const handleTablesReorder = (list) => {
    settings.reorderProjectTables(list);
  };

  return (
    <>
      <ActionButton
        square
        id="projectSettings"
        icon={<Settings />}
        color="secondary"
        onClick={handleVisibility}
        name={t("views.editor.dialogs.project_settings.title")}
        tooltip
      />
      <Dialog
        open={visible}
        onClose={handleVisibility}
        width={460}
        title={t("views.editor.dialogs.project_settings.title")}
      >
        <Grid item container direction="column">
          <InfoLabel
            label="views.editor.dialogs.project_settings.tables"
            tooltipText="views.editor.dialogs.project_settings.tables_tooltip"
          />
          <SortableListV2
            list={usedTables}
            itemIdKey="name"
            group="projectSettings"
            onListReorder={handleTablesReorder}
            useDragHandle
            lockAxis
            itemContainerProps={{ className: "py-1" }}
            direction="column"
          >
            {usedTables.map(({ name, visible, setVisibility }) => (
              <SettingEntry
                key={name}
                name={name}
                visible={visible}
                label={t(`proposal_parts.editor_sections.${name}`)}
                setVisibility={handleTableVisibility(
                  name,
                  visible,
                  setVisibility
                )}
              />
            ))}
          </SortableListV2>
        </Grid>
      </Dialog>
      <FeatureTimelineAlert />
      <FeatureDescriptionAlert />
    </>
  );
});

EstimateSettings.propTypes = {
  allowEdition: bool,
};